<svelte:options customElement={{ tag: "bt-inline-pagination" }} />

<script lang="ts">
	import { onMount } from 'svelte';
	import { eventBus } from '$shared/lib/eventBus';
	import Icon from '$shared/icons/Icon.svelte';

	export let numPages: string | number = "0";
	numPages = +numPages;
	export let paginationId: string = '';

	let activePageIndex: number = 0;

	let loadedChildPages = new Set();

	const paginateTo = (targetPageIndex: number) => {
		eventBus.emit('inline-pagination-change',
			{targetPageIndex: targetPageIndex, paginationId: paginationId}
		);
		activePageIndex = targetPageIndex;
	}

	onMount(() => {
		paginateTo(0);
		return () => {
			eventBus.off('inline-pagination-page-ready');
			loadedChildPages.clear();
		}
	});

	// Enforce loading of first page on load by waiting for all page components to be ready
	// This currently means that we paginate twice. It was working in testing without this in local but not in storybook
	eventBus.on('inline-pagination-page-ready', event => {
		if (event.paginationId === paginationId) {
			loadedChildPages.add(event.pageIndex);
			if (loadedChildPages.size === numPages) {
				paginateTo(0);
			}
		}
	});
</script>

{#if numPages > 1}
	<nav aria-label="Learner Guide pagination" class="controls">
		<ul>
			{#if activePageIndex !== 0}
				<li class="prev">
					<button
						type="button"
						aria-label="Go to previous prompt"
						on:click={()=>{ paginateTo(activePageIndex - 1) }}
					>
						<Icon icon="ChevronLeftAlt" size="medium"></Icon>
					</button>
				</li>
			{/if}
				<li class="current" aria-current="page" aria-live="polite" aria-atomic="true">
					<span class="screen-reader-only">Prompt </span>{activePageIndex + 1} of {numPages}
				</li>
			{#if activePageIndex !== numPages - 1}
				<li class="next">
					<button
						type="button"
						aria-label={`Go to prompt ${activePageIndex + 1}`}
						on:click={()=>{ paginateTo(activePageIndex + 1) }}>
							<Icon icon="ChevronRightAlt" size="medium"></Icon>
					</button>
				</li>
			{/if}
		</ul>
	</nav>
{/if}

<slot name="pages"></slot>

<style>
	.screen-reader-only {
		clip: rect(0 0 0 0);
	  clip-path: inset(50%);
	  height: 1px;
	  overflow: hidden;
	  position: absolute;
	  white-space: nowrap;
	  width: 1px;
	}
	nav.controls {
		width: 100%;
		margin-bottom: theme('spacing.4');
		ul {
			list-style-type: none;
			margin: 0;
			margin-block-start: 0;
			margin-block-end: 0;
			padding: 0;
			display: grid;
			grid-template-columns: theme('size.6') 1fr theme('size.6');
			grid-template-rows: 1fr;
		}
		li {
			display: block;
		}
		li.prev {
			grid-column-start: 1;
			grid-column-end: 2;
		}
		li.current {
			grid-column-start: 2;
			grid-column-end: 3;
			text-align: center;
			font-size: theme('fontSize.base');
			font-weight: 500;
		}
		li.next {
			grid-column-start: 3;
			grid-column-end: 4;
		}
		li.prev, li.next {
			display: grid;
			place-content: center;
		}
	}
	button {
		width: theme('size.6');
		height: theme('size.6');
		margin: 0;
		padding: 0;
		border: none;
		background: transparent;
		display: block;
		&:hover {
			cursor: pointer;
			color: theme('colors.neutral.500');
		}
	}
</style>
